import React from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  Play, Sword, Globe, Sparkles, Users, Crown, Dices, 
  BookOpen, Scroll, Zap
} from 'lucide-react';
import { Button } from '../components/ui/button';
import MatrixRain from '../components/MatrixRain';

export default function Home() {
  const navigate = useNavigate();

  const universes = [
    { name: 'Star Wars', image: 'https://customer-assets.emergentagent.com/job_41857d1d-1d1e-47df-90b3-d2aa6c5d2539/artifacts/ywfgiehf_image.png', pos: 'top', description: 'Galactic saga' },
    { name: 'Game of Thrones', image: 'https://static.prod-images.emergentagent.com/jobs/41857d1d-1d1e-47df-90b3-d2aa6c5d2539/images/832dd0d28f5042d868cd4082e27fe3ee187ee0fd23fae8beb018db4656c0c2a3.png', pos: '50% 30%', description: 'Westeros politics' },
    { name: 'Lord of the Rings', image: 'https://static.prod-images.emergentagent.com/jobs/41857d1d-1d1e-47df-90b3-d2aa6c5d2539/images/b0ea9e37a8fd385713e663486a95ff78dd6307b918cc294ea81ce8c0b9927a14.png', pos: 'center', description: 'Middle-earth' },
  ];

  const features = [
    { icon: <Sparkles className="h-6 w-6" />, title: 'AI Game Master', description: '9 advanced AI models to choose from' },
    { icon: <Globe className="h-6 w-6" />, title: '4+ Universes', description: 'Lore-accurate storytelling' },
    { icon: <Users className="h-6 w-6" />, title: 'NPC Tracking', description: 'Auto-generated character cards' },
    { icon: <Dices className="h-6 w-6" />, title: 'Dice Roller', description: 'Integrated D20 mechanics' },
    { icon: <BookOpen className="h-6 w-6" />, title: 'Auto-Summaries', description: 'Every 20 messages' },
    { icon: <Scroll className="h-6 w-6" />, title: 'Character Sheets', description: 'Track stats & inventory' },
  ];

  return (
    <div className="min-h-screen bg-black relative">
      <MatrixRain opacity={0.06} speed={0.8} />

      {/* Hero Section */}
      <div className="relative min-h-[70vh] flex items-center justify-center overflow-hidden z-10">
        <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/60 to-black" />

        <div className="relative z-10 max-w-5xl mx-auto px-4 sm:px-6 text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <h1 className="text-4xl sm:text-5xl md:text-7xl font-bold text-blue-400 glow-text mb-6 font-mono tracking-wider">
              TaleMatrix
            </h1>
            
            <p className="text-xl text-blue-300/70 mb-4 font-mono">
              AI-Powered Tabletop RPG Experience
            </p>
            
            <p className="text-base text-blue-200/40 mb-12 max-w-3xl mx-auto">
              Choose your universe. Create your character. Let AI guide your adventure.
              From Warhammer to Middle-earth, every story is unique.
            </p>

            <Button
              onClick={() => navigate('/character/create')}
              size="lg"
              className="bg-blue-500 hover:bg-blue-400 text-black font-bold uppercase tracking-widest px-8 sm:px-12 py-4 sm:py-6 text-base sm:text-lg rounded-none shadow-[0_0_20px_rgba(96,165,250,0.4)] hover:shadow-[0_0_40px_rgba(96,165,250,0.6)] transition-all hover:scale-105 font-mono border border-blue-400"
              data-testid="begin-campaign-btn"
            >
              <Play className="mr-2 h-6 w-6" />
              Begin Your Campaign
            </Button>
          </motion.div>
        </div>
      </div>

      {/* Universes Section */}
      <div className="relative py-20 px-6 z-10">
        <div className="max-w-7xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h2 className="text-3xl font-bold text-blue-400 mb-4 font-mono">
              <Globe className="inline mr-3 h-8 w-8" />
              7 Universes to Explore
            </h2>
            <p className="text-blue-200/40 text-base">
              Each with strict lore accuracy and timeline adherence
            </p>
          </motion.div>

          <div className="grid md:grid-cols-3 gap-4">
            {universes.map((universe, index) => (
              <motion.div
                key={universe.name}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="glass-card rounded-none border-blue-500/10 hover:border-blue-400/40 transition-all text-center group cursor-pointer overflow-hidden"
              >
                {universe.image ? (
                  <div className="w-full h-40 overflow-hidden">
                    <img src={universe.image} alt={universe.name} style={{ objectPosition: universe.pos || 'center' }} className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-300" />
                  </div>
                ) : (
                  <div className="text-4xl pt-6 pb-3 group-hover:scale-110 transition-transform">
                    {universe.icon}
                  </div>
                )}
                <div className="p-4">
                  <h3 className="text-base font-bold text-blue-300 mb-2 font-mono">
                    {universe.name}
                  </h3>
                  <p className="text-blue-200/40 text-sm">
                    {universe.description}
                  </p>
                </div>
              </motion.div>
            ))}
            {/* Warhammer 40K centered below */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.6 }}
              className="glass-card rounded-none border-blue-500/10 hover:border-blue-400/40 transition-all text-center group cursor-pointer overflow-hidden md:col-start-2"
            >
              <div className="w-full h-40 overflow-hidden">
                <img src="https://static.prod-images.emergentagent.com/jobs/41857d1d-1d1e-47df-90b3-d2aa6c5d2539/images/146e5a6bfde7411e9b0270d1a1731dc28e9ee069c21e3d38fa67ecdd8eada52b.png" alt="Warhammer 40K" style={{ objectPosition: 'top' }} className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-300" />
              </div>
              <div className="p-4">
                <h3 className="text-base font-bold text-blue-300 mb-2 font-mono">Warhammer 40K</h3>
                <p className="text-blue-200/40 text-sm">Grimdark future</p>
              </div>
            </motion.div>
          </div>
        </div>
      </div>

      {/* Features Section */}
      <div className="relative py-20 px-6 z-10">
        <div className="max-w-7xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h2 className="text-3xl font-bold text-blue-400 mb-4 font-mono">
              <Zap className="inline mr-3 h-8 w-8" />
              Powerful Features
            </h2>
            <p className="text-blue-200/40 text-base">
              Everything you need for an immersive RPG experience
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {features.map((feature, index) => (
              <motion.div
                key={feature.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="glass-card p-6 rounded-none border-blue-500/10 hover:border-blue-400/40 transition-all group"
              >
                <div className="text-blue-400 mb-4 group-hover:scale-110 transition-transform">
                  {feature.icon}
                </div>
                <h3 className="text-lg font-bold text-blue-300 mb-2 font-mono">
                  {feature.title}
                </h3>
                <p className="text-blue-200/40 text-sm">
                  {feature.description}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </div>

      {/* AI Models Section */}
      <div className="relative py-20 px-6 z-10">
        <div className="max-w-5xl mx-auto text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="text-3xl font-bold text-blue-400 mb-6 font-mono">
              <Crown className="inline mr-3 h-8 w-8" />
              Choose Your AI Game Master
            </h2>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-4 mb-12">
              {['GPT-5.2', 'Claude Sonnet 4.5', 'Gemini 3 Flash', 'Claude Opus 4.5', 'Gemini 3 Pro', 'Cydonia 24B', 'Grok 4.1 Fast'].map((model) => (
                <div key={model} className="glass-card p-4 rounded-none border border-blue-500/15 hover:border-blue-400/40 transition-all">
                  <p className="text-blue-300 font-mono text-sm">{model}</p>
                </div>
              ))}
            </div>
          </motion.div>
        </div>
      </div>

      {/* CTA Section */}
      <div className="relative py-24 px-6 z-10">
        <div className="max-w-4xl mx-auto text-center">
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            className="glass-card p-12 rounded-none magic-border"
          >
            <Sword className="h-14 w-14 text-blue-400 mx-auto mb-6" />
            <h2 className="text-4xl md:text-5xl font-bold text-blue-400 mb-6 font-mono">
              Your Adventure Awaits
            </h2>
            <p className="text-lg text-blue-300/50 mb-8">
              Join the next generation of tabletop RPG storytelling
            </p>
            <Button
              onClick={() => navigate('/character/create')}
              size="lg"
              className="bg-blue-500 hover:bg-blue-400 text-black font-bold uppercase tracking-widest px-8 sm:px-12 py-4 sm:py-6 text-base sm:text-lg rounded-none shadow-[0_0_20px_rgba(96,165,250,0.4)] hover:shadow-[0_0_40px_rgba(96,165,250,0.6)] transition-all hover:scale-105 font-mono border border-blue-400"
            >
              <Play className="mr-2 h-6 w-6" />
              Start Playing Now
            </Button>
          </motion.div>
        </div>
      </div>
    </div>
  );
}
